import { z } from 'zod';
import { CONTROL_POLICIES } from '../registries/policies.ts';
import { SCREENING_STATUSES } from '../registries/statuses.ts';
import { AdmissionStatusSchema, JoinPolicySchema } from './admission.ts';
import { AspectRatio, AssetSummary } from './library.ts';
import { DisplayName, DurationMs, Id, IsoDateTime, Title } from './primitives.ts';

export const ControlPolicySchema = z.enum(CONTROL_POLICIES);
export const ScreeningStatusSchema = z.enum(SCREENING_STATUSES);

/** What every participant of a room may know about the screening. */
export const ScreeningPublic = z.object({
  id: Id,
  title: z.string(),
  startsAt: IsoDateTime,
  status: ScreeningStatusSchema,
  controlPolicy: ControlPolicySchema,
  joinPolicy: JoinPolicySchema,
  assetId: Id,
});
export type ScreeningPublic = z.infer<typeof ScreeningPublic>;

/** What hosts see in the studio. */
export const ScreeningSummary = ScreeningPublic.extend({
  asset: AssetSummary.pick({
    id: true,
    title: true,
    status: true,
    posterUrl: true,
    posterAspectRatio: true,
    durationMs: true,
  }),
  /** Absolute invite link to share. */
  inviteUrl: z.url(),
  createdAt: IsoDateTime,
  endedAt: IsoDateTime.nullable(),
  /** Guests waiting for a host's decision right now. */
  pendingAdmissions: z.number().int().nonnegative(),
});
export type ScreeningSummary = z.infer<typeof ScreeningSummary>;

export const ScreeningListQuery = z.object({ status: ScreeningStatusSchema.default('open') });

export const CreateScreeningBody = z.object({
  /** Defaults to the film title. */
  title: Title.optional(),
  assetId: Id,
  startsAt: IsoDateTime,
  controlPolicy: ControlPolicySchema.optional(),
  /** Defaults to DEFAULT_JOIN_POLICY. */
  joinPolicy: JoinPolicySchema.optional(),
});

/**
 * Changing the film resets playback to the start, paused. Switching to a join policy that needs no
 * approval admits everyone who is still waiting.
 */
export const UpdateScreeningBody = z
  .object({
    title: Title,
    assetId: Id,
    startsAt: IsoDateTime,
    controlPolicy: ControlPolicySchema,
    joinPolicy: JoinPolicySchema,
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update' });

/** Public preview of an invite, shown before joining. */
export const InvitePreview = z.object({
  screening: ScreeningPublic.pick({ id: true, title: true, startsAt: true, status: true, joinPolicy: true }),
  film: z.object({ title: z.string(), durationMs: DurationMs.nullable() }),
  /** Same-origin poster URL that works with the invite code alone, or null. */
  posterUrl: z.string().nullable(),
  posterAspectRatio: AspectRatio.nullable(),
  viewer: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('anonymous') }),
    z.object({ kind: z.literal('account'), displayName: z.string() }),
    z.object({
      kind: z.literal('guest'),
      displayName: z.string(),
      /** Kept for older clients: equals `admission === 'admitted'`. */
      admitted: z.boolean(),
      /** Null until the guest asks to join this screening. */
      admission: AdmissionStatusSchema.nullable(),
      banned: z.boolean(),
    }),
  ]),
});
export type InvitePreview = z.infer<typeof InvitePreview>;

/** `displayName` is required for anonymous visitors and renames an existing guest otherwise. */
export const JoinInviteBody = z.object({ displayName: DisplayName.optional() });

/**
 * Joining asks to enter. Accounts and screenings that need no approval are admitted at once; otherwise
 * the guest waits (`pending`) and polls `getInvite` until a host decides.
 */
export const JoinInviteResult = z.object({ screeningId: Id, admission: AdmissionStatusSchema });
